import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Loader2, Sparkles, Check, AlertCircle, CheckSquare, RefreshCcw, CalendarDays } from 'lucide-react';
import type { Email } from '@/types/email';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CreationPopover } from './CreationPopover';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTodoActionsStore } from '@/stores/todoActionsStore';
import { useTodoStore } from '@/stores/todoStore';
import { useMessageTaskSuggestions, type TaskSuggestionEntry } from '@/hooks/useMessageTaskSuggestions';
import { cn } from '@/lib/utils';
import type { UseMessageSummaryResult } from '@/hooks/useMessageSummary';
import { CalendarPopoverContent } from '@/features/todo/components/forms/CalendarPopoverContent';
import { dueQuickOptions } from '@/features/todo/utils/dateOptions';
import { formatDateOnly, parseDateOnly } from '@/features/todo/utils/dateHelpers';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

// Marked options configured inline where needed

interface EmailTaskCreationContextValue {
  email: Email;
  summary: UseMessageSummaryResult;
  taskSuggestions: ReturnType<typeof useMessageTaskSuggestions>;
  openPopover: (
    anchor: HTMLElement | { getBoundingClientRect: () => DOMRect; contextElement?: Element },
    options?: PopoverPositioningOptions
  ) => void;
  closePopover: () => void;
  todoReady: boolean;
  todoLoading: boolean;
  popoverState: {
    open: boolean;
    align: 'start' | 'center' | 'end';
    side: 'top' | 'bottom' | 'left' | 'right';
    width: number | null;
    anchorRef: React.RefObject<{ getBoundingClientRect: () => DOMRect; contextElement?: Element } | null>;
  };
}

type PopoverPositioningOptions = {
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom' | 'left' | 'right';
  width?: number;
};

const EmailTaskCreationContext = createContext<EmailTaskCreationContextValue | null>(null);

export function EmailTaskCreationProvider({
  email,
  summary,
  children,
}: {
  email: Email;
  summary: UseMessageSummaryResult;
  children: ReactNode;
}) {
  const taskSuggestions = useMessageTaskSuggestions(email);
  const [anchorState, setAnchorState] = useState<{
    element: HTMLElement | { getBoundingClientRect: () => DOMRect; contextElement?: Element } | null;
    key: number;
  }>({
    element: null,
    key: 0,
  });
  const anchor = anchorState.element;
  const [align, setAlign] = useState<'start' | 'center' | 'end'>('end');
  const [side, setSide] = useState<'top' | 'bottom' | 'left' | 'right'>('bottom');
  const [width, setWidth] = useState<number | null>(640);
  const anchorRef = useRef<{ getBoundingClientRect: () => DOMRect; contextElement?: Element } | null>(null);
  
  useEffect(() => {
    anchorRef.current = anchor
      ? {
          getBoundingClientRect: () => anchor.getBoundingClientRect(),
          contextElement: anchor instanceof HTMLElement ? anchor : anchor.contextElement,
        }
      : null;
  }, [anchorState.key, anchor]);
  const autoRequestRef = useRef(false);
  const todoInitialized = useTodoStore((state) => state.initialized);
  const todoLoading = useTodoStore((state) => state.loading);
  const bootstrapTodo = useTodoActionsStore((state) => state.bootstrap);

  useEffect(() => {
    if (!anchor) {
      autoRequestRef.current = false;
    }
  }, [anchorState.key, anchor]);

  useEffect(() => {
    if (!anchor) {
      return;
    }
    if (todoInitialized || todoLoading) {
      return;
    }
    void bootstrapTodo();
  }, [anchorState.key, anchor, todoInitialized, todoLoading, bootstrapTodo]);

  const closePopover = useCallback(() => {
    setAnchorState((prev) => ({ ...prev, element: null }));
  }, []);

  const { annotation: suggestionAnnotation, suggestions, isLoading: suggestionsLoading, requestSuggestions } =
    taskSuggestions;
  const suggestionsLength = suggestions.length;

  const openPopover = useCallback(
    (target: HTMLElement | { getBoundingClientRect: () => DOMRect; contextElement?: Element }, options?: PopoverPositioningOptions) => {
      setAlign(options?.align ?? 'end');
      setSide(options?.side ?? 'bottom');
      setWidth(options?.width ?? 640);
      const element =
        'getBoundingClientRect' in target ? target : (target as HTMLElement);
      setAnchorState({ element, key: Date.now() });

      const hasSuggestionData =
        (suggestionAnnotation && suggestionsLength > 0) || suggestionsLength > 0;
      if (!hasSuggestionData && !suggestionsLoading && !autoRequestRef.current) {
        autoRequestRef.current = true;
        void requestSuggestions();
      }
    },
    [suggestionAnnotation, suggestionsLength, suggestionsLoading, requestSuggestions]
  );

  useEffect(() => {
    if (!anchor) {
      return;
    }
    if (suggestionsLoading) {
      return;
    }
    const hasSuggestionData = (suggestionAnnotation && suggestionsLength > 0) || suggestionsLength > 0;
    if (hasSuggestionData) {
      autoRequestRef.current = true;
      return;
    }
    if (autoRequestRef.current) {
      return;
    }
    autoRequestRef.current = true;
    void requestSuggestions();
  }, [anchorState.key, anchor, suggestionAnnotation, suggestionsLength, suggestionsLoading, requestSuggestions]);

  const contextValue = useMemo<EmailTaskCreationContextValue>(
    () => ({
      email,
      summary,
      taskSuggestions,
      openPopover,
      closePopover,
      todoReady: todoInitialized,
      todoLoading,
      popoverState: {
        open: Boolean(anchor),
        align,
        side,
        width,
        anchorRef,
      },
    }),
    [
      email,
      summary,
      taskSuggestions,
      openPopover,
      closePopover,
      anchor,
      align,
      side,
      width,
      anchorRef,
      todoInitialized,
      todoLoading,
    ]
  );

  return (
    <EmailTaskCreationContext.Provider value={contextValue}>
      {children}
      <EmailTaskCreationPopover />
    </EmailTaskCreationContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useEmailTaskCreation() {
  const ctx = useContext(EmailTaskCreationContext);
  if (!ctx) {
    throw new Error('EmailTaskCreationContext unavailable');
  }
  return ctx;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalEmailTaskCreation() {
  return useContext(EmailTaskCreationContext);
}

function buildGeneralSuggestions(subject: string, emailId: string): TaskSuggestionEntry[] {
  const normalizedSubject = subject?.trim() || 'this email';
  const base = normalizedSubject.length > 90 ? `${normalizedSubject.slice(0, 87)}…` : normalizedSubject;
  const candidates = Array.from(
    new Set([
      `Look at ${base}`,
      `Reply to ${base}`,
      `Follow up on ${base}`,
      `Buy ${base}`,
      base,
    ])
  );
  return candidates.map((title, index) => ({
    id: `subject-${emailId}-${index}`,
    title,
    dueDate: null,
    dueText: null,
    bullets: [],
    notes: '',
    confidence: null,
  }));
}

function EditableSuggestionCard({
  suggestion,
  created,
  creating,
  error,
  onCreate,
  disabled = false,
  disabledReason,
  leadingIcon,
}: {
  suggestion: TaskSuggestionEntry;
  created: boolean;
  creating: boolean;
  error: string | null;
  onCreate: (payload: { title: string; dueDate: string | null }) => Promise<void>;
  disabled?: boolean;
  disabledReason?: string | null;
  leadingIcon?: ReactNode;
}) {
  const [title, setTitle] = useState(suggestion.title);
  const [dueDate, setDueDate] = useState<string | null>(suggestion.dueDate ?? null);
  const notes = suggestion.notes?.trim() ?? '';
  const bullets = suggestion.bullets;
  const notesContainBullets =
    Boolean(notes) && bullets.length > 0 && bullets.every((bullet) => notes.includes(bullet));
  const shouldShowBullets = bullets.length > 0 && !notesContainBullets;
  const isActionDisabled = creating || created || disabled;
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const parsedDueDate = useMemo(() => parseDateOnly(dueDate), [dueDate]);

  const dueDateLabel = useMemo(() => {
    if (!dueDate) {
      return '';
    }
    const parsed = new Date(`${dueDate}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return dueDate;
    }
    return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }, [dueDate]);

  useEffect(() => {
    if (isActionDisabled && datePickerOpen) {
      setDatePickerOpen(false);
    }
  }, [isActionDisabled, datePickerOpen]);

  useEffect(() => {
    setTitle(suggestion.title);
    setDueDate(suggestion.dueDate ?? null);
    setDatePickerOpen(false);
  }, [suggestion.id, suggestion.title, suggestion.dueDate]);

  const handleDateSelect = useCallback(
    (value?: Date) => {
      if (!value) {
        return;
      }
      setDueDate(formatDateOnly(value));
      setDatePickerOpen(false);
    },
    []
  );

  const handleQuickSelect = useCallback((getDate: () => Date) => {
    setDueDate(formatDateOnly(getDate()));
    setDatePickerOpen(false);
  }, []);

  const handleClearDate = useCallback(() => {
    setDueDate(null);
    setDatePickerOpen(false);
  }, []);

  return (
    <div className="rounded-2xl border border-border/60 bg-card/80 p-4 space-y-3">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {leadingIcon ? (
            <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              {leadingIcon}
            </span>
          ) : null}
          <Input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Task title"
            className="text-sm font-medium flex-1 min-w-[180px]"
            disabled={isActionDisabled}
          />
          <div className="flex flex-shrink-0 items-center gap-2">
            <Popover
              open={datePickerOpen}
              onOpenChange={(open) => {
                if (isActionDisabled) {
                  setDatePickerOpen(false);
                  return;
                }
                setDatePickerOpen(open);
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant={datePickerOpen ? 'secondary' : 'outline'}
                  size="sm"
                  className="h-9 gap-0 rounded-full border-border/70"
                  disabled={isActionDisabled}
                >
                  {!dueDate && <CalendarDays className="h-4 w-4" />}
                  <span className="text-xs font-medium">{dueDateLabel}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                side="bottom"
                sideOffset={8}
                className="w-auto border-none bg-transparent p-0 shadow-none"
                onEscapeKeyDown={(e) => e.stopPropagation()}
              >
                <CalendarPopoverContent
                  selected={parsedDueDate}
                  onSelect={handleDateSelect}
                  quickOptions={dueQuickOptions}
                  onQuickSelect={handleQuickSelect}
                  onClear={handleClearDate}
                  onClose={() => setDatePickerOpen(false)}
                />
              </PopoverContent>
            </Popover>
            <Button
              size="sm"
              onClick={() => onCreate({ title: title.trim() || suggestion.title, dueDate })}
              disabled={isActionDisabled}
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                </>
              ) : created ? (
                <>
                  <Check className="h-4 w-4" />
                </>
              ) : (
                'Add'
              )}
            </Button>
          </div>
        </div>
        {disabled && disabledReason ? (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            {disabledReason}
          </p>
        ) : error ? (
          <p className="text-xs text-destructive flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {error}
          </p>
        ) : null}
      </div>
      {shouldShowBullets ? (
        <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
          {bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      ) : null}
      {notes ? <MarkdownPreview content={notes} /> : null}
    </div>
  );
}

function MarkdownPreview({ content }: { content: string }) {
  const html = useMemo(() => {
    if (!content.trim()) {
      return '';
    }
    const raw = marked.parse(content);
    const normalized = typeof raw === 'string' ? raw : '';
    return DOMPurify.sanitize(normalized);
  }, [content]);

  if (!html) {
    return null;
  }

  return (
    <div
      className="text-sm leading-relaxed text-muted-foreground [&_a]:text-primary [&_a]:underline [&_a]:break-all [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function EmailTaskCreationPopover() {
  const ctx = useContext(EmailTaskCreationContext);
  const createTask = useTodoActionsStore((state) => state.createTask);
  const [created, setCreated] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [generalSuggestions, setGeneralSuggestions] = useState<TaskSuggestionEntry[]>([]);
  const [initialEmailId, setInitialEmailId] = useState<string | null>(null);

  if (!ctx) {
    throw new Error('EmailTaskCreationContext unavailable');
  }

  const { email, taskSuggestions, popoverState, closePopover, todoReady, todoLoading } = ctx;

  useEffect(() => {
    if (initialEmailId !== email.id) {
      setInitialEmailId(email.id);
      setCreated(new Set());
      setErrors({});
      setGeneralSuggestions(buildGeneralSuggestions(email.subject || '', email.id));
    }
  }, [email.id, email.subject, initialEmailId]);

  const lists = useTodoStore((state) => state.lists);
  const listOrder = useTodoStore((state) => state.listOrder);

  // Get inbox list ID for tasks created from emails
  const inboxListId = listOrder.find((id) => lists[id]?.isInbox) ?? null;

  const handleCreate = useCallback(
    async (source: 'ai' | 'subject', suggestion: TaskSuggestionEntry, overrides: { title: string; dueDate: string | null }) => {
      const suggestionId = `${source}-${suggestion.id}`;
      setErrors((prev) => ({ ...prev, [suggestionId]: null }));
      setPending((prev) => {
        const next = new Set(prev);
        next.add(suggestionId);
        return next;
      });
      try {
        const dueDate = overrides.dueDate ? overrides.dueDate : suggestion.dueDate ?? null;
        await createTask({
          title: overrides.title || suggestion.title,
          dueDate: dueDate ?? null,
          notesMarkdown: source === 'ai' ? suggestion.notes : undefined,
          emails: [{ accountId: email.accountId, messageId: email.id }],
          listId: inboxListId,
        });
        setCreated((prev) => {
          const next = new Set(prev);
          next.add(suggestionId);
          return next;
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to create task';
        setErrors((prev) => ({ ...prev, [suggestionId]: message }));
        throw error;
      } finally {
        setPending((prev) => {
          const next = new Set(prev);
          next.delete(suggestionId);
          return next;
        });
      }
    },
    [createTask, email.accountId, email.id, inboxListId]
  );

  const handleGeneralCreate = useCallback(
    async (suggestion: TaskSuggestionEntry, overrides: { title: string; dueDate: string | null }) => {
      const suggestionId = `subject-${suggestion.id}`;
      setErrors((prev) => ({ ...prev, [suggestionId]: null }));
      setPending((prev) => {
        const next = new Set(prev);
        next.add(suggestionId);
        return next;
      });
      try {
        await createTask({
          title: overrides.title || suggestion.title,
          dueDate: overrides.dueDate ?? null,
          emails: [{ accountId: email.accountId, messageId: email.id }],
          listId: inboxListId,
        });
        setCreated((prev) => {
          const next = new Set(prev);
          next.add(suggestionId);
          return next;
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to create task';
        setErrors((prev) => ({ ...prev, [suggestionId]: message }));
      } finally {
        setPending((prev) => {
          const next = new Set(prev);
          next.delete(suggestionId);
          return next;
        });
      }
    },
    [createTask, email.accountId, email.id, inboxListId]
  );

  const allSuggestions = taskSuggestions.suggestions;
  const combinedSuggestions = useMemo(
    () => [
      ...allSuggestions.map((suggestion) => ({ source: 'ai' as const, suggestion })),
      ...generalSuggestions.map((suggestion) => ({ source: 'subject' as const, suggestion })),
    ],
    [allSuggestions, generalSuggestions]
  );

  return (
    <CreationPopover
      open={popoverState.open}
      onClose={closePopover}
      title="Create task"
      headerActions={
        <Button
          size="sm"
          variant="secondary"
          className="gap-2"
          onClick={() => taskSuggestions.requestSuggestions({ force: true })}
          disabled={taskSuggestions.isLoading}
        >
          <RefreshCcw className={cn('h-4 w-4', taskSuggestions.isLoading && 'animate-spin')} />
          {taskSuggestions.isLoading ? 'Working…' : 'Refresh'}
        </Button>
      }
      anchorRef={popoverState.anchorRef}
      align={popoverState.align}
      side={popoverState.side}
      desktopStyle={popoverState.width ? { width: popoverState.width } : undefined}
    >
      <div className="space-y-3">
              {taskSuggestions.error ? (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {taskSuggestions.error}
                </p>
              ) : null}
              {taskSuggestions.isLoading && !allSuggestions.length ? (
                <div className="flex h-24 items-center justify-center text-sm text-muted-foreground gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating suggestions…
                </div>
              ) : null}
              {!combinedSuggestions.length && !taskSuggestions.isLoading ? (
                <p className="text-xs text-muted-foreground">No suggestions available for this email.</p>
              ) : null}
              {combinedSuggestions.map(({ source, suggestion }) => {
                const suggestionId = `${source}-${suggestion.id}`;
                return (
                  <EditableSuggestionCard
                    key={suggestionId}
                    suggestion={suggestion}
                    created={created.has(suggestionId)}
                    creating={pending.has(suggestionId)}
                    error={errors[suggestionId] ?? null}
                    disabled={!todoReady}
                    disabledReason={
                      !todoReady
                        ? todoLoading
                          ? 'Preparing tasks workspace…'
                          : 'Finish loading tasks before creating'
                        : null
                    }
                    leadingIcon={source === 'ai' ? <Sparkles className="h-4 w-4" /> : undefined}
                    onCreate={({ title, dueDate }) =>
                      source === 'ai'
                        ? handleCreate('ai', suggestion, { title, dueDate })
                        : handleGeneralCreate(suggestion, { title, dueDate })
                    }
                  />
                );
              })}
      </div>
    </CreationPopover>
  );
}

export function EmailTaskCreationIconButton({
  side = 'bottom',
  align = 'end',
  className,
  variant = 'ghost',
  size = 'icon',
}: {
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  className?: string;
  variant?: 'outline' | 'ghost';
  size?: 'icon' | 'sm';
}) {
  const ctx = useOptionalEmailTaskCreation();
  if (!ctx) {
    return null;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={className}
          onClick={(event) => ctx.openPopover(event.currentTarget, { align, side })}
        >
          <CheckSquare className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Create task</TooltipContent>
    </Tooltip>
  );
}
